import {
  ApiOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Badge, Tag, Tooltip } from 'antd';
import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import { LANGUAGES } from '../../../../utils/const/languages';
import { MAPPING_TYPE_UI } from '../../../../utils/const/mapping';

/**
 * Génère la configuration des colonnes pour la table des attributs
 * @param {Array} rawAttributesDatas - Liste des attributs bruts pour afficher les labels
 * @returns {Array} Configuration des colonnes pour le composant Table
 */
export const getAttributeColumns = (rawAttributesDatas = []) => [
  {
    title: 'Champ cible',
    key: 'target_field_label',
    sorter: (a, b) => a.target_field_label.localeCompare(b.target_field_label),
    render: (record) => {
      return (
        <Badge
          key={record.target_field_label}
          color={record.requirement?.color}
          text={
            <Tooltip
              title={record.requirement?.label}
              color={record.requirement?.color}
              placement="left"
            >
              {/* translate=yes: permet la traduction navigateur sur le libellé champ cible sans bloquer tout le tableau */}
              <span translate="yes">{record.target_field_label}</span>
            </Tooltip>
          }
        />
      );
    },
  },
  {
    title: 'Champ source',
    key: 'source_field',
    sorter: (a, b) => {
      const aField = a.source_field || '';
      const bField = b.source_field || '';
      return aField.localeCompare(bField);
    },
    render: (record) => {
      if (!record.source_field) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#bababa', marginLeft: 4 }}>_</span>
          </div>
        );
      }

      // Handle multiple source fields separated by comma
      const sourceFields = record.source_field.split(',').map((f) => f.trim());
      const hasMore = sourceFields.length > 2;
      const displayedFields = hasMore ? sourceFields.slice(0, 2) : sourceFields;
      const remainingCount = sourceFields.length - 2;

      // Get all labels for tooltip
      const allLabels = sourceFields
        .map((field) => {
          const attr = rawAttributesDatas.find((a) => a.name === field);
          return attr?.label || field;
        })
        .join(', ');

      const content = (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            gap: 4,
          }}
        >
          <span>
            {displayedFields
              .map((field) => {
                const attr = rawAttributesDatas.find((a) => a.name === field);
                return attr?.label || field;
              })
              .join(', ')}
            {hasMore && (
              <span style={{ color: '#1890ff', marginLeft: 4 }}>
                +{remainingCount} champ{remainingCount > 1 ? 's' : ''}
              </span>
            )}
          </span>
          <span style={{ color: '#bababa', fontSize: 12 }}>
            {displayedFields.join(', ')}
            {hasMore && ` +${remainingCount}`}
          </span>
        </div>
      );

      if (hasMore) {
        return (
          <Tooltip
            title={
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  Tous les champs ({sourceFields.length})
                </div>
                <div>{allLabels}</div>
                <div style={{ color: '#999', fontSize: 11, marginTop: 4 }}>
                  {sourceFields.join(', ')}
                </div>
              </div>
            }
          >
            {content}
          </Tooltip>
        );
      }

      return content;
    },
  },
  {
    title: 'Type de mapping',
    key: 'transformation_type',
    sorter: (a, b) => {
      const aType = a.transformation_type || '';
      const bType = b.transformation_type || '';
      return aType.localeCompare(bType);
    },
    render: (record) => {
      const config =
        MAPPING_TYPE_UI[record.transformation_type] || MAPPING_TYPE_UI.none;

      const iconStyle = {
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
      };

      const icons = {
        chain: <UnorderedListOutlined style={iconStyle} />,
        unique: <ApiOutlined style={iconStyle} />,
        simple: <LinkOutlined style={iconStyle} />,
        none: <CloseCircleOutlined style={iconStyle} />,
      };

      const icon = icons[record.transformation_type] || icons.none;

      return (
        <Tag
          color={config.color}
          icon={icon}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {config.label}
        </Tag>
      );
    },
  },
  {
    title: 'Langue',
    dataIndex: 'lang',
    key: 'lang',
    sorter: (a, b) => {
      const aLang = a.lang || '';
      const bLang = b.lang || '';
      return aLang.localeCompare(bLang);
    },
    render: (value) => {
      const lang = LANGUAGES.find((l) => l.code === value);
      if (!lang)
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="gray">Pas de langue</Tag>
          </div>
        );

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ReactCountryFlag
            countryCode={lang.countryCode}
            svg
            style={{
              width: '1.5em',
              height: '1.5em',
            }}
          />
          {lang.label}
        </div>
      );
    },
  },
];
